import { useState, useEffect } from 'react';

interface UserSessionData {
  fullName: string;
  email: string;
  language?: string;
  behavioralCompleted?: boolean;
  companyId?: string;
}

export const useUserSession = () => {
  const [sessionData, setSessionData] = useState<UserSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSessionData = () => {
      const stored = sessionStorage.getItem('userSessionData');
      if (stored) {
        try {
          const parsedData = JSON.parse(stored);
          setSessionData(parsedData);
        } catch (error) {
          console.error('Error parsing session data:', error);
          sessionStorage.removeItem('userSessionData');
          setSessionData(null);
        }
      } else {
        setSessionData(null);
      }
      setIsLoading(false);
    };

    // Load initial data
    loadSessionData();

    // Always update local session when Supabase auth changes
    const handleAuthChange = () => {
      const supabaseUser = (window as any)?.supabaseUser;
      if (supabaseUser) {
        const fullName = (supabaseUser.user_metadata && (supabaseUser.user_metadata.full_name || supabaseUser.user_metadata.name)) || 'User';
        const email = supabaseUser.email;
        
        // Preserve existing session data, especially companyId
        const existingSession: Partial<UserSessionData> = sessionData || {};
        
        // Check for registrationCompanyId as fallback
        const registrationCompanyId = sessionStorage.getItem('registrationCompanyId');
        
        const merged: UserSessionData = {
          ...existingSession, // Preserve existing data
          fullName,
          email,
          // Preserve companyId or use registration fallback
          companyId: existingSession.companyId || registrationCompanyId || undefined,
        };
        
        console.log('useUserSession: handleAuthChange preserving session data');
        setSessionData(merged);
        sessionStorage.setItem("userSessionData", JSON.stringify(merged));
      }
    };

    // Listen for localStorage changes from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userSessionData') {
        loadSessionData();
      }
    };

    // Listen for logout event to clear session storage
    const handleLogout = () => {
      setSessionData(null);
      sessionStorage.removeItem('userSessionData');
      localStorage.removeItem('preferredLanguage');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-logout', handleLogout);
    window.addEventListener('auth-state-change', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-logout', handleLogout);
      window.removeEventListener('auth-state-change', handleAuthChange);
    };
  }, []);

  const saveUserSession = (data: UserSessionData) => {
    setSessionData(data);
    sessionStorage.setItem('userSessionData', JSON.stringify(data));
    // Also save language preference separately for the language context
    if (data.language) {
      localStorage.setItem('preferredLanguage', data.language);
    }
  };

  const clearUserSession = () => {
    setSessionData(null);
    sessionStorage.removeItem('userSessionData');
    localStorage.removeItem('preferredLanguage');
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('auth-logout'));
  };

  const updateSession = (updates: Partial<UserSessionData>) => {
    if (sessionData) {
      const updated = { ...sessionData, ...updates };
      saveUserSession(updated);
    }
  };

  return {
    sessionData,
    isLoading,
    saveUserSession,
    clearUserSession,
    updateSession
  };
};
